import { Fragment, type FunctionComponent, StrictMode, useEffect } from "react";
import { Checkbox } from "@App/Components/common/Checkbox";
import { Organization, PremiumService, Service } from "@App/types";
import { useTranslation } from "react-i18next";
import {
  OrganizationHasPremiumServicesVisibleInList,
  PremiumServiceIsReplacementOrPickup,
  PremiumServiceIsVisibleInList,
} from "@App/utils/conditionals/premiumServices";
import {
  ServiceIsWheelRelatedAndOrganizationIsMeinAuto,
  ServiceIsWheelRelatedAndOrganizationIsMobilityConcept,
} from "@App/utils/conditionals/combinations";
import { usePremiumServiceUtils } from "@App/hooks/usePremiumServiceUtils";
import { useDriverData } from "@App/hooks/useDriverData";
import { OrganizationIsArval, OrganizationIsALD } from "@App/utils/conditionals/organization";
import { useOrganizationPremiumServices } from "@App/hooks/api/useOrganizationPremiumServices";
import { PremiumServiceTypes } from "@App/types/enums";
import { TechnicalRepairHint } from "@App/Components/ClientSpecific/Ayvens/TechnicalRepairHint";

type PremiumServiceFilterProps = {
  organization: Organization;
  service: Service;
  premiumServices?: PremiumService[];
  className?: string;
  onLoaded?: (hasItems: boolean) => void;
};

type PremiumServiceFilterState = {
  premiumServices?: PremiumService[];
  typing: boolean;
  inputTimeout: ReturnType<typeof setTimeout>;
};

export const PremiumServiceFilter: FunctionComponent<PremiumServiceFilterProps> = (
  props: PremiumServiceFilterProps,
  state: PremiumServiceFilterState,
) => {
  const { t } = useTranslation();

  const { togglePremiumService, togglePremiumServiceCarReplacementOrPickup, toggled } =
    usePremiumServiceUtils();

  const {
    requiredServices,
    requiredPremiumServices,
    optionalPremiumServices,
    prohibitedPremiumServices,
  } = useDriverData();

  const existsInServices = (serviceArray: PremiumService[], premiumService: PremiumService) => {
    return serviceArray.some((p) => p.type === premiumService.type);
  };

  const { loading, data } = useOrganizationPremiumServices();

  useEffect(() => {
    if (!loading && data) {
      props.onLoaded &&
        props.onLoaded(OrganizationHasPremiumServicesVisibleInList(data.organization));
    }
  }, [loading, data]);

  if (loading) {
    return <div />;
  }

  const selectablePremiumServices =
    data.organization.premium_services?.filter(
      (ps) => PremiumServiceIsVisibleInList(ps) && ps.service_type === "comfort-service",
    ) ?? [];

  const comfortServices = (premiumServices) => {
    if (!premiumServices) {
      return [];
    }
    return premiumServices.filter(
      (premiumService) =>
        premiumService.service_type === "comfort-service" &&
        !prohibitedPremiumServices.some((p) => p.type === premiumService.type),
    );
  };

  // For Arval, we don't show the sub-service services as a filter in the list
  const subsServices = OrganizationIsArval(props.organization)
    ? []
    : (data.organization.premium_services?.filter(
        (premiumService) => premiumService.service_type === "sub-service",
      ) ?? []);

  const exactlyOneSubserviceIsSelected = () => {
    return (
      subsServices.filter((subService) => existsInServices(props.premiumServices, subService))
        .length === 1
    );
  };

  const isAyvensCheckboxDisabled = (service: PremiumService) => {
    const toggledSubServices = subsServices.filter((sub) => toggled(sub));

    // Condition 1: Count how many toggled subservices are NOT type "Uvv"
    const subservicesNotUvvCount = toggledSubServices.filter(
      (s) => s.type !== PremiumServiceTypes.UVV,
    ).length;
    const hasOneOrLessNonUvvService = subservicesNotUvvCount <= 1;

    // Condition 2: TRUE only for this specific service if IT is toggled
    const isToggled = toggled(service);

    // True if both conditions are satisfied it returns true
    return hasOneOrLessNonUvvService && isToggled;
  };

  const isTechnicalRepairSelected = props.premiumServices.some(
    (ps) => ps.type === PremiumServiceTypes.TECHNICAL_REPAIR,
  );

  return (
    <StrictMode>
      {OrganizationIsALD(props.organization) && (
        <Fragment>
          {(requiredServices?.length > 0 || requiredPremiumServices?.length > 0) && (
            <>
              <div className="font-bold text-sm mb-3">{t("driverDataStep.mandatory")}</div>
              {requiredServices.map((service) => (
                <Checkbox
                  key={service.id}
                  status={true}
                  title={service.name}
                  onChange={() => {
                    return;
                  }}
                  disabled={true}
                />
              ))}
              {requiredPremiumServices.map((service) => (
                <Checkbox
                  key={service.id}
                  status={true}
                  title={service.name}
                  onChange={() => {
                    return;
                  }}
                  disabled={true}
                />
              ))}
            </>
          )}
          {optionalPremiumServices?.length > 0 && (
            <>
              <div className="font-bold text-sm mb-3 mt-8">{t("Gewählte Services")}</div>
              {optionalPremiumServices.map((service) => (
                <Checkbox
                  key={service.id}
                  status={toggled(service)}
                  title={service.name}
                  disabled={isAyvensCheckboxDisabled(service)}
                  onChange={() => togglePremiumService(service)}
                />
              ))}
            </>
          )}

          {comfortServices(data.organization.premium_services)?.length > 0 && (
            <>
              <div className={`font-bold text-sm mb-3 ${subsServices?.length > 0 ? "mt-8" : ""}`}>
                {comfortServices(data.organization.premium_services).find(
                  (premiumService) => premiumService.paid,
                )
                  ? t("premiumServices.additionalServices")
                  : t("premiumServices.additionalServicesNoStar")}
              </div>
              {comfortServices(data.organization.premium_services)?.map((premiumService) => (
                <Fragment key={"fragment-" + premiumService.id}>
                  <Checkbox
                    key={premiumService.id}
                    status={toggled(premiumService)}
                    title={premiumService.name + (premiumService.paid ? "*" : "")}
                    onChange={() =>
                      PremiumServiceIsReplacementOrPickup(premiumService)
                        ? togglePremiumServiceCarReplacementOrPickup(premiumService)
                        : togglePremiumService(premiumService)
                    }
                  />
                </Fragment>
              ))}
            </>
          )}

          {isTechnicalRepairSelected && <TechnicalRepairHint className={"!mt-8"} />}
        </Fragment>
      )}

      {!OrganizationIsALD(props.organization) && (
        <Fragment>
          {props.organization.settings.garage_list.filters.includes("services") && (
            <Fragment>
              {subsServices?.length > 0 && (
                <div className="font-bold text-sm mb-3">{t("Gewählte Services")}</div>
              )}
              {subsServices?.map((premiumService) => (
                <Fragment key={"fragment-" + premiumService.id}>
                  <Checkbox
                    key={premiumService.id}
                    status={toggled(premiumService)}
                    title={premiumService.name + (premiumService.paid ? "*" : "")}
                    disabled={toggled(premiumService) && exactlyOneSubserviceIsSelected()}
                    onChange={() =>
                      PremiumServiceIsReplacementOrPickup(premiumService)
                        ? togglePremiumServiceCarReplacementOrPickup(premiumService)
                        : togglePremiumService(premiumService)
                    }
                  />
                </Fragment>
              ))}
              {selectablePremiumServices?.length > 0 && (
                <div className={`font-bold text-sm mb-3 ${subsServices?.length > 0 ? "mt-8" : ""}`}>
                  {selectablePremiumServices.find((premiumService) => premiumService.paid)
                    ? t("premiumServices.additionalServices")
                    : t("premiumServices.additionalServicesNoStar")}
                </div>
              )}
              {selectablePremiumServices?.map((premiumService) => (
                <Fragment key={"fragment-" + premiumService.id}>
                  <Checkbox
                    key={premiumService.id}
                    status={toggled(premiumService)}
                    title={premiumService.name + (premiumService.paid ? "*" : "")}
                    onChange={() =>
                      PremiumServiceIsReplacementOrPickup(premiumService)
                        ? togglePremiumServiceCarReplacementOrPickup(premiumService)
                        : togglePremiumService(premiumService)
                    }
                  />
                </Fragment>
              ))}
              {selectablePremiumServices.length > 0 &&
                selectablePremiumServices.find((premiumService) => premiumService.paid) && (
                  <p className={"text-xs mt-2 mb-5 lg:mb-0"}>
                    {ServiceIsWheelRelatedAndOrganizationIsMeinAuto(
                      data.organization,
                      props.service,
                    ) &&
                      t("garageList.filters.premiumServices.additional-cost-infoTireStorageList")}
                    {ServiceIsWheelRelatedAndOrganizationIsMobilityConcept(
                      data.organization,
                      props.service,
                    ) &&
                      t("garageList.filters.premiumServices.additional-cost-mightNotBeInContract")}
                    {!ServiceIsWheelRelatedAndOrganizationIsMobilityConcept(
                      props.organization,
                      props.service,
                    ) &&
                      !ServiceIsWheelRelatedAndOrganizationIsMeinAuto(
                        props.organization,
                        props.service,
                      ) &&
                      t("garageList.filters.premiumServices.additional-cost-info")}
                  </p>
                )}
            </Fragment>
          )}
        </Fragment>
      )}
    </StrictMode>
  );
};
